import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, users as usersApi } from '../api';
import { FiUsers, FiMail, FiShield, FiCalendar, FiLock, FiX, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { PASSWORD_HINT, validatePassword } from '../utils/passwordPolicy';

function Users() {
  const [currentUser, setCurrentUser] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetNotice, setResetNotice] = useState('');
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncNotice, setSyncNotice] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const me = await auth.getCurrentUser();
      setCurrentUser(me);
      if (!me.is_admin) {
        setLoading(false);
        return;
      }
      const users = await usersApi.getAll();
      setUsersList(users);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.detail || 'Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  };

  const openResetModal = (user) => {
    setResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetNotice('');
  };

  const closeResetModal = () => {
    setResetUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword !== confirmPassword) {
      setResetError('Пароли не совпадают');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setResetError(validationError);
      return;
    }

    setResetLoading(true);
    try {
      const email = resetUser.email;
      await usersApi.resetPassword(resetUser.id, newPassword);
      closeResetModal();
      setResetNotice(`Пароль для ${email} обновлён`);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Не удалось сменить пароль');
    } finally {
      setResetLoading(false);
    }
  };

  const openDeleteModal = (user) => {
    setDeleteUser(user);
    setDeleteError('');
    setResetNotice('');
  };

  const closeDeleteModal = () => {
    setDeleteUser(null);
    setDeleteError('');
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;

    setDeleteLoading(true);
    setDeleteError('');
    try {
      const result = await usersApi.deleteUser(deleteUser.id);
      closeDeleteModal();
      setResetNotice(
        `${result.message}. Аннулировано бронирований: ${result.cancelled_bookings}`
      );
      await loadData();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Не удалось удалить пользователя');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  if (!currentUser?.is_admin) {
    return <Navigate to="/" replace />;
  }

  const activeCount = usersList.filter((u) => u.is_active).length;
  const adminCount = usersList.filter((u) => u.is_admin).length;

  const canDeleteUser = (user) => {
    if (user.id === currentUser?.id) return false;
    if (user.is_admin && adminCount <= 1) return false;
    return true;
  };

  const handleSyncBitrix = async () => {
    setSyncLoading(true);
    setSyncNotice('');
    setError('');
    try {
      const result = await usersApi.syncBitrixIds();
      const unmatched = result.unmatched?.length || 0;
      setSyncNotice(
        `Bitrix: сопоставлено ${result.matched} из ${result.app_users_total}`
        + (result.updated ? `, обновлено ${result.updated}` : '')
        + (unmatched ? `. Без пары: ${unmatched}` : '')
      );
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось синхронизировать Bitrix ID');
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FiUsers className="h-7 w-7 text-primary-600" />
            Пользователи
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Все зарегистрированные пользователи системы бронирования
          </p>
        </div>
        <button
          type="button"
          onClick={handleSyncBitrix}
          disabled={syncLoading}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`mr-2 h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
          Синхр. Bitrix ID
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Всего</p>
          <p className="text-2xl font-semibold text-gray-900">{usersList.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Активных</p>
          <p className="text-2xl font-semibold text-green-700">{activeCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Администраторов</p>
          <p className="text-2xl font-semibold text-amber-700">{adminCount}</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {resetNotice && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {resetNotice}
        </div>
      )}

      {syncNotice && (
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
          {syncNotice}
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ФИО</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bitrix ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Роль</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Бронирования</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Регистрация</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersList.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {user.full_name}
                    {user.id === currentUser.id && (
                      <span className="ml-2 text-xs text-gray-400">(вы)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <span className="inline-flex items-center">
                      <FiMail className="mr-1 h-4 w-4 text-gray-400" />
                      {user.email}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {user.bitrix_user_id ? (
                      <span className="font-mono text-gray-800">{user.bitrix_user_id}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {user.is_admin ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <FiShield className="mr-1 h-3 w-3" />
                        Администратор
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Менеджер
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {user.is_active ? (
                      <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Активен
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Заблокирован
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <span className="inline-flex items-center">
                      <FiCalendar className="mr-1 h-4 w-4 text-gray-400" />
                      {user.bookings_total}
                      {user.bookings_active > 0 && (
                        <span className="ml-1 text-green-700">({user.bookings_active} акт.)</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {format(new Date(user.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <div className="flex flex-col sm:flex-row gap-1">
                      <button
                        type="button"
                        onClick={() => openResetModal(user)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
                      >
                        <FiLock className="mr-1 h-3 w-3" />
                        Сбросить пароль
                      </button>
                      {canDeleteUser(user) && (
                        <button
                          type="button"
                          onClick={() => openDeleteModal(user)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                        >
                          <FiTrash2 className="mr-1 h-3 w-3" />
                          Удалить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {usersList.length === 0 && !error && (
          <div className="text-center py-12 text-gray-500">
            <FiUsers className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p>Пользователей пока нет</p>
          </div>
        )}
      </div>

      <p className="mt-4 text-xs text-gray-500">
        Найдено: <strong>{usersList.length}</strong> {usersList.length === 1 ? 'пользователь' : usersList.length >= 2 && usersList.length <= 4 ? 'пользователя' : 'пользователей'}
      </p>

      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 my-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Сброс пароля</h3>
                <p className="text-sm text-gray-600 mt-1">{resetUser.full_name} ({resetUser.email})</p>
              </div>
              <button
                type="button"
                onClick={closeResetModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {resetError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {resetError}
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Новый пароль</label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">{PASSWORD_HINT}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Подтвердите пароль</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:text-sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeResetModal}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Закрыть
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
                >
                  {resetLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteUser && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 my-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Удалить пользователя</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {deleteUser.full_name} ({deleteUser.email})
                </p>
              </div>
              <button
                type="button"
                onClick={closeDeleteModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-700 mb-4">
              Все активные бронирования будут аннулированы с комментарием
              «аннулировано Администратором», остатки вернутся на склад.
              {deleteUser.bookings_active > 0 && (
                <span className="block mt-2 font-medium text-amber-700">
                  Активных бронирований: {deleteUser.bookings_active}
                </span>
              )}
            </p>

            {deleteError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {deleteError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
